import React, { useLayoutEffect, useRef, useState } from 'react';
import { AvTooltip, filterOperatorOptions, operatorColors } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';

export const FieldFilterBoxSelectExpression = ({ rawExpression }) => {
  const operator = rawExpression.and ? 'and' : rawExpression.or ? 'or' : undefined;

  if (!operator) {
    return null;
  }

  const operands = rawExpression[operator];
  const [visibleOperands, setVisibleOperands] = useState(operands.length);
  const [remainingCount, setRemainingCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const calculateOperandsLength = () => {
      if (containerRef.current && containerRef.current.parentElement) {
        const moreTagWidth = 40;
        const operandWidths = Array.from(containerRef.current?.querySelectorAll('.operand-item')).map(el => el.clientWidth);
        const parentEl = containerRef.current.parentElement;
        const computedStyle = window.getComputedStyle(parentEl);
        const gap = parseFloat(computedStyle.gap) || 0;
        const containerWidth =
          parentEl.clientWidth - (parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)) - gap;

        if (operandWidths.length > 0) {
          // eslint-disable-next-line custom-rules/no-let
          let operandsCount = 0;
          // eslint-disable-next-line custom-rules/no-let
          let totalOperandsWidth = 0;

          // eslint-disable-next-line no-restricted-syntax
          for (const operandWidth of operandWidths) {
            if (containerWidth - moreTagWidth - gap < totalOperandsWidth + operandWidth + gap) {
              break;
            }

            totalOperandsWidth += operandWidth;
            operandsCount++;
          }

          setVisibleOperands(operandsCount);
          setRemainingCount(operands.length - operandsCount);
        }
      }
    };

    calculateOperandsLength();
    const resizeObserver = new ResizeObserver(calculateOperandsLength);

    if (containerRef?.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, operands]);

  const renderOperand =
    (shouldRenderHidden?: boolean) =>
    // eslint-disable-next-line react/no-unstable-nested-components
    ({ value, operator: op }, index) => (
      <Box
        className="operand-item"
        sx={{
          ...flex.row,
          gap: '4px',
          ...(!shouldRenderHidden && index >= visibleOperands ? { visibility: 'hidden', position: 'absolute' } : {}),
        }}
        key={value}>
        {index > 0 && <span style={{ fontWeight: 600, color: operatorColors[operator!] }}>{operator.toUpperCase()}</span>}
        <Box sx={{ ...flex.row, gap: '4px' }}>
          <Box sx={{ fontWeight: 600 }}>{filterOperatorOptions.string.find(({ value }) => value === op)?.title}</Box>
          {typeof value === 'string' ? <div>&quot;{value}&quot;</div> : null}
        </Box>
      </Box>
    );

  return (
    <Box ref={containerRef} sx={{ ...flex.row }}>
      <Box sx={{ ...flex.row, gap: '4px', color: 'var(--color-content-base-primary)', mr: visibleOperands > 0 ? '8px' : 0 }}>
        {operands.map(renderOperand())}
      </Box>
      {remainingCount > 0 && (
        <AvTooltip
          title={
            <Box
              sx={{
                ...flex.row,
                gap: '4px',
                flexWrap: 'wrap',
                backgroundColor: 'var(--color-surface-immutable-darkGrey)',
                color: 'var(--color-content-immutable-white)',
              }}>
              {operands.map(renderOperand(true))}
            </Box>
          }>
          <Box
            key="operands-chip"
            sx={{
              backgroundColor: 'var(--color-surface-interactive-secondary-default)',
              color: 'var(--color-content-base-secondary)',
              px: '6px',
              borderRadius: '20px',
              fontWeight: 500,
            }}>
            +{remainingCount}
          </Box>
        </AvTooltip>
      )}
    </Box>
  );
};
